import { API_URL } from "@/config";
import history from "@/router/history";
import {
  LoginResponse,
  LoginResponseSchema,
  RefreshTokenRequest,
} from "@/types/auth";
import i18next from "@/utils/i18n";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-toastify";

export const client = axios.create({
  baseURL: API_URL,
});

export const clientRefreshToken = axios.create({
  baseURL: API_URL,
});

function authRequestInterceptor(config: InternalAxiosRequestConfig<unknown>) {
  if (config.url === "/auth/login") {
    config.headers.Authorization = null;
    return config;
  }
  const tokenString = localStorage.getItem("token");
  //TODO: try catch this
  try {
    if (tokenString == null) {
      throw new Error("emptyToken when performing request");
    }
    const tokenObject: LoginResponse = JSON.parse(tokenString);

    config.headers.Accept = "application/json";

    if (tokenObject) {
      const token: LoginResponse = LoginResponseSchema.parse(tokenObject);
      if (token?.expiresAt > new Date()) {
        config.headers.Authorization = `Bearer ${token.accessToken}`;
      } else {
        config.headers.Authorization = null;
      }
    }
  } catch (error) {
    config.headers.Authorization = null;
    console.warn(`The stored token wasn't our: ${tokenString}`, error);
  }

  return config;
}

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: AxiosResponse) => void;
  reject: (error?: AxiosError) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
let refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

clientRefreshToken.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

client.interceptors.request.use(authRequestInterceptor, (error: AxiosError) =>
  Promise.reject(error)
);
client.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig;

    if (
      error?.response?.status === HttpStatusCode.Unauthorized &&
      originalRequest.url !== "/auth/login"
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Get the refresh token from storage
          const tokenString = localStorage.getItem("token");

          if (!tokenString) {
            throw new Error(
              "token is empty when trying to load if to get a refreshToken"
            );
          }

          // Call the refresh token API
          const token: LoginResponse = JSON.parse(tokenString);

          const request: RefreshTokenRequest = {
            refreshToken: token.refreshToken,
          };

          const refreshTokenResponse: LoginResponse = LoginResponseSchema.parse(
            await clientRefreshToken.post(`/auth/refreshtoken`, request)
          );

          // Store the new access token
          localStorage.setItem("token", JSON.stringify(refreshTokenResponse));

          // Retry the original request with the new token
          client.defaults.headers[
            "Authorization"
          ] = `Bearer ${refreshTokenResponse.accessToken}`;

          // Update the request headers with the new access token
          if (error.config) {
            error.config.headers[
              "Authorization"
            ] = `Bearer ${refreshTokenResponse.accessToken}`;
          }

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            client
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue = [];

          // Retry the original request
          return client(originalRequest);
        } catch (refreshError) {
          if (!history.location?.pathname.startsWith("/login")) {
            history.replace("/logout");
            //We didn't managed to use the refreshToken, so the user should log again
            toast.error(i18next.t("toast.generic.error.unauthorized"), {
              toastId: "unauthorizeToast",
            });
          }
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise((resolve, reject) => {
        refreshAndRetryQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    if (HttpStatusCode.Forbidden === error.response?.status) {
      toast.error(i18next.t("toast.generic.error.forbidden"), {
        toastId: "forbiddenToast",
      });
    }

    if (HttpStatusCode.InternalServerError === error.response?.status) {
      toast.error(i18next.t("toast.generic.error.internalServerError"), {
        toastId: "internalServerErrorToast",
      });
    }

    return Promise.reject(error);
  }
);
